import { assertNever } from './chainResponseState';

/**
 * How much of the layout row the chat column gets. Resolved once, in
 * precedence order, so the column never has to re-derive it from booleans.
 */
export type ChatColumnWidth =
  /** The side panel is mid-entrance; the push keyframe owns the width. */
  | { kind: 'animating' }
  /**
   * Both the reasoning/dynamic-view side panel and a docked pane (each a
   * fixed-px flex sibling) are open at once — a builder conversation that
   * opens its reasoning sidebar. The chat gives up BOTH, so the row doesn't
   * overflow and push the docked pane off-screen.
   */
  | {
      kind: 'side-panel-with-docked-pane';
      panelWidthPx: number;
      paneWidthPx: number;
    }
  /**
   * Reasoning / dynamic-view panel open — a docked flex sibling sized by the
   * shared slideout width (same as the docked pane): the chat takes the
   * remainder.
   */
  | { kind: 'side-panel'; panelWidthPx: number }
  /**
   * A docked pane (proposal or builder record) is a real flex sibling sized
   * by the shared slideout width — the chat gives up exactly that.
   */
  | { kind: 'docked-pane'; paneWidthPx: number }
  /**
   * Overlay fallback (narrow viewport, or this page couldn't claim the inline
   * host): the chat shrinks beside the portaled ProposalSidebar.
   */
  | { kind: 'proposal-overlay'; overlayWidthPx: number }
  | { kind: 'full' };

export interface ResolveChatColumnWidthParams {
  isAnimating: boolean;
  isSidePanelOpen: boolean;
  sidePanelWidthPx: number;
  hasDockedPane: boolean;
  dockedPaneWidthPx: number;
  isProposalOverlayVisible: boolean;
  proposalOverlayWidthPx: number;
}

export function resolveChatColumnWidth({
  isAnimating,
  isSidePanelOpen,
  sidePanelWidthPx,
  hasDockedPane,
  dockedPaneWidthPx,
  isProposalOverlayVisible,
  proposalOverlayWidthPx,
}: ResolveChatColumnWidthParams): ChatColumnWidth {
  if (isAnimating) {
    return { kind: 'animating' };
  }
  // Both a side panel and a docked pane can be open together (they are distinct
  // flex siblings), so this must precede the individual cases — resolving to
  // just the side panel would leave the docked pane's width unaccounted for and
  // overflow the row.
  if (isSidePanelOpen && hasDockedPane) {
    return {
      kind: 'side-panel-with-docked-pane',
      panelWidthPx: sidePanelWidthPx,
      paneWidthPx: dockedPaneWidthPx,
    };
  }
  if (isSidePanelOpen) {
    return { kind: 'side-panel', panelWidthPx: sidePanelWidthPx };
  }
  if (hasDockedPane) {
    return { kind: 'docked-pane', paneWidthPx: dockedPaneWidthPx };
  }
  if (isProposalOverlayVisible) {
    return { kind: 'proposal-overlay', overlayWidthPx: proposalOverlayWidthPx };
  }
  return { kind: 'full' };
}

/** CSS width for the column; undefined while the push animation owns it. */
export function toCssWidth(width: ChatColumnWidth): string | undefined {
  switch (width.kind) {
    case 'animating':
      return undefined;
    case 'side-panel':
      return `calc(100% - ${width.panelWidthPx}px)`;
    case 'side-panel-with-docked-pane':
      return `calc(100% - ${width.panelWidthPx + width.paneWidthPx}px)`;
    case 'docked-pane':
      return `calc(100% - ${width.paneWidthPx}px)`;
    case 'proposal-overlay':
      return `calc(100% - ${width.overlayWidthPx}px)`;
    case 'full':
      return '100%';
    default:
      return assertNever(width);
  }
}
